import styled from "@emotion/styled"
import { PText } from "@porsche-design-system/components-react"
import { spacingStaticXs, spacingStaticSm, spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { Tooltip } from "../../tooltip/Tooltip"
import { Checkbox } from "./Checkbox"

export interface CheckboxGroupItem {
	disabledHint?: string
	label: string
	value: string
}

export interface CheckboxGroupProps {
	className?: string
	disabledHint?: string
	error?: string
	inline?: boolean
	items: CheckboxGroupItem[]
	label?: string
	onChange: (value: string[], changedValue: string, type: "select" | "deselect") => void
	value: string[]
}

export const CheckboxGroup = ({
	disabledHint,
	error,
	inline,
	items,
	label,
	onChange,
	value = [],
	...rest
}: CheckboxGroupProps) => {
	const isGroupDisabled = disabledHint !== undefined
	const input = (
		<>
			{label && <Label color={isGroupDisabled ? "contrast-medium" : undefined}>{label}</Label>}
			<CheckboxList $inline={!!inline}>
				{items.map((item, index) => {
					return (
						<Checkbox
							key={index}
							state={error ? "error" : undefined}
							checked={value.includes(item.value)}
							disabledHint={isGroupDisabled ? "" : item.disabledHint}
							onChange={(newValue) =>
								onChange(
									newValue ? [...value, item.value] : value.filter((v) => v !== item.value),
									item.value,
									newValue ? "select" : "deselect",
								)
							}
						>
							{item.label}
						</Checkbox>
					)
				})}
			</CheckboxList>
		</>
	)

	return isGroupDisabled ? (
		<Wrapper {...rest}>
			<Tooltip content={disabledHint}>{input}</Tooltip>
		</Wrapper>
	) : (
		<div {...rest}>{input}</div>
	)
}

const Wrapper = styled.div`
	display: inline-block;
	justify-self: start; /* to prevent full width element in flex or grid causing tooltip to appear in the middle */
`

const Label = styled(PText)`
	margin-bottom: ${spacingStaticXs};
`

const CheckboxList = styled("div", { shouldForwardProp })<{ $inline: boolean }>`
	display: flex;
	flex-direction: ${({ $inline }) => ($inline ? "row" : "column")};
	flex-wrap: ${({ $inline }) => ($inline ? "wrap" : undefined)};
	justify-content: start;
	gap: ${({ $inline }) => ($inline ? `${spacingStaticSm} ${spacingStaticMd}` : spacingStaticSm)};
`
